import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { Logo } from '../common/Logo';
import {
  HomeIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  SunIcon,
  MoonIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface ChatNavBarProps {
  isSidebarOpen?: boolean;
  onToggleSidebar?: () => void;
  isMobile?: boolean;
}

const ChatNavBar: React.FC<ChatNavBarProps> = ({
  isSidebarOpen = false,
  onToggleSidebar,
  isMobile = false,
}) => {
  const { isDarkMode, toggleDarkMode } = useTheme();
  const location = useLocation();

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Library', href: '/library', icon: BookOpenIcon },
    { name: 'Chat', href: '/chat', icon: ChatBubbleLeftRightIcon },
  ];

  return (
    <nav className={`chat-nav-bar ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
      {/* Mobile hamburger menu - integrated into navbar */}
      {isMobile && onToggleSidebar && (
        <button
          onClick={onToggleSidebar}
          className="mobile-menu-btn"
          aria-label={isSidebarOpen ? 'Close menu' : 'Open menu'}
        >
          {isSidebarOpen ? (
            <XMarkIcon className="menu-icon" />
          ) : (
            <Bars3Icon className="menu-icon" />
          )}
        </button>
      )}

      {/* Logo */}
      <Link to="/dashboard" className="nav-logo">
        <Logo variant="app-icon" size={32} />
        <span className="logo-text">vidya.ai</span>
      </Link>

      {/* Navigation Links */}
      <div className="nav-links">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`nav-link ${isActive ? 'active' : ''}`}
            >
              <item.icon className="nav-icon" />
              <span className="nav-label">{item.name}</span>
            </Link>
          );
        })}
      </div>

      {/* Theme Toggle */}
      <button
        onClick={toggleDarkMode}
        className="theme-button"
        title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDarkMode ? <SunIcon /> : <MoonIcon />}
      </button>

      <style>{`
        .chat-nav-bar {
          position: sticky;
          top: 0;
          z-index: 50;
          display: flex;
          align-items: center;
          gap: 12px;
          height: 52px;
          padding: 0 16px;
          background: rgba(15, 23, 42, 0.95);
          border-bottom: 1px solid rgba(100, 116, 139, 0.2);
          backdrop-filter: blur(12px);
          flex-shrink: 0;
        }

        .chat-nav-bar.light-theme {
          background: rgba(255, 255, 255, 0.95);
          border-bottom: 1px solid rgba(209, 213, 219, 0.5);
        }

        /* Mobile Menu Button - integrated into navbar */
        .mobile-menu-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          padding: 0;
          border: none;
          border-radius: 10px;
          background: rgba(100, 116, 139, 0.15);
          cursor: pointer;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .light-theme .mobile-menu-btn {
          background: rgba(107, 114, 128, 0.1);
        }

        .mobile-menu-btn:hover {
          background: rgba(6, 182, 212, 0.2);
        }

        .light-theme .mobile-menu-btn:hover {
          background: rgba(6, 182, 212, 0.15);
        }

        .mobile-menu-btn .menu-icon {
          width: 20px;
          height: 20px;
          color: #94A3B8;
          transition: color 0.2s;
        }

        .light-theme .mobile-menu-btn .menu-icon {
          color: #6B7280;
        }

        .mobile-menu-btn:hover .menu-icon {
          color: #06B6D4;
        }

        /* Logo */
        .nav-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
          margin-right: 32px;
        }

        .logo-text {
          font-size: 18px;
          font-weight: 700;
          color: #F8FAFC;
        }

        .light-theme .logo-text {
          color: #1F2937;
        }

        /* Navigation Links */
        .nav-links {
          display: flex;
          align-items: center;
          gap: 4px;
          flex: 1;
        }

        .nav-link {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          border-radius: 10px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          color: #94A3B8;
          transition: all 0.2s;
        }

        .light-theme .nav-link {
          color: #6B7280;
        }

        .nav-link:hover {
          background: rgba(100, 116, 139, 0.15);
          color: #E2E8F0;
        }

        .light-theme .nav-link:hover {
          background: rgba(107, 114, 128, 0.1);
          color: #1F2937;
        }

        .nav-link.active {
          background: rgba(6, 182, 212, 0.1);
          color: #06B6D4;
          border: 1px solid rgba(6, 182, 212, 0.2);
        }

        .light-theme .nav-link.active {
          background: rgba(6, 182, 212, 0.08);
          color: #0891B2;
          border: 1px solid rgba(6, 182, 212, 0.25);
        }

        .nav-icon {
          width: 18px;
          height: 18px;
        }

        .nav-label {
          display: block;
        }

        /* Theme Button */
        .theme-button {
          padding: 8px;
          background: rgba(100, 116, 139, 0.2);
          border: none;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .light-theme .theme-button {
          background: rgba(107, 114, 128, 0.1);
        }

        .theme-button:hover {
          background: rgba(6, 182, 212, 0.2);
        }

        .light-theme .theme-button:hover {
          background: rgba(6, 182, 212, 0.15);
        }

        .theme-button svg {
          width: 20px;
          height: 20px;
          color: #94A3B8;
        }

        .light-theme .theme-button svg {
          color: #6B7280;
        }

        .theme-button:hover svg {
          color: #06B6D4;
        }

        /* Mobile Responsive - matches 768px breakpoint used elsewhere */
        @media (max-width: 768px) {
          .chat-nav-bar {
            padding: 0 12px;
            gap: 12px;
          }

          .nav-logo {
            margin-right: auto; /* Push theme button to right */
          }

          .logo-text {
            display: none;
          }

          /* Hide nav links on mobile - sidebar handles navigation */
          .nav-links {
            display: none;
          }
        }
      `}</style>
    </nav>
  );
};

export default ChatNavBar;
